<template>
  <div v-click-outside="closeOptions" class="tree-selector">
    <div v-if="label" class="tree-selector__top">
      <div class="tree-selector__label">
        {{ label }}
        <span v-if="required" class="tree-selector__required">*</span>
      </div>
    </div>
    <div class="tree-selector__wrapper">
      <div
        class="tree-selector__input"
        @click="
          hasOptions = !hasOptions;
          focused = !focused;
        "
      >
        <div class="tree-selector__left">
          <div class="tree-selector__search-icon">
            <SearchIcon />
          </div>
          <input
            v-if="focused"
            ref="searchInput"
            v-model.trim="search"
            class="tree-selector__title--search"
            :placeholder="searchPlaceholder"
            type="text"
            @click.stop
          />
          <p v-else-if="valueTitle" class="tree-selector__title">
            {{ valueTitle }}
          </p>
          <p v-else class="tree-selector__title tree-selector__title--placeholder">
            {{ placeholder }}
          </p>
        </div>
        <div
          class="tree-selector__dropdown-icon"
          :class="{ 'tree-selector__dropdown-icon_active': hasOptions }"
        >
          <DropdownIcon />
        </div>
      </div>
      <div v-show="hasOptions" class="tree-selector__options">
        <p class="tree-selector__count">{{ valueCount }}</p>
        <TreeOptions
          v-for="item in filteredOptions"
          :key="item[idKey] + item[nameKey]"
          :value="value"
          :option="item"
          :only-children="onlyChildren"
          :use-radio="useRadio"
          @optionSelected="optionSelected"
          @optionDeselected="optionDeselected"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TreeOptions from './TreeOptions.vue';
import SearchIcon from '@/components/Icons/SearchIcon.vue';
import DropdownIcon from '@/components/Icons/DropdownIcon.vue';
import declinationOfNum from '@/functions/declinationOfNum';
import vClickOutside from 'v-click-outside';
import { cloneDeep } from 'lodash';

export default {
  components: {
    SearchIcon,
    DropdownIcon,
    TreeOptions
  },
  directives: {
    clickOutside: vClickOutside.directive
  },
  props: {
    value: {
      type: Array,
      required: true
    },
    placeholder: {
      type: String,
      required: true
    },
    searchPlaceholder: {
      type: String,
      default: 'Поиск'
    },
    label: {
      type: String,
      default: undefined
    },
    options: {
      type: Array,
      required: true
    },
    idKey: {
      type: String,
      default: 'id'
    },
    nameKey: {
      type: String,
      default: 'name'
    },
    childrenKey: {
      type: String,
      default: 'children'
    },
    required: {
      type: Boolean,
      default: false
    },
    declinations: {
      type: Array,
      required: true
    },
    onlyChildren: {
      type: Boolean,
      default: false
    },
    useRadio: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      hasOptions: false,
      focused: false,
      search: ''
    };
  },
  computed: {
    valueCount() {
      return this.value.length
        ? `Выбрано: ${this.value.length} ${declinationOfNum(this.value.length, this.declinations)}`
        : 'Не выбрано';
    },
    valueTitle() {
      return this.value.length
        ? this.value
            .reduce((prev, curr) => prev + this.findOptionName(curr, this.options) + '; ', '')
            .slice(0, -2)
        : '';
    },
    filteredOptions() {
      const regex = new RegExp(this.search, 'i');
      return this.options
        ? cloneDeep(this.options).filter(option => this.recursiveFilter(option, regex))
        : [];
    }
  },
  watch: {
    focused(value) {
      if (value) {
        this.$nextTick(() => this.$refs.searchInput.focus());
      }
    }
  },
  methods: {
    optionSelected(value) {
      value && this.setValue(value);
    },
    optionDeselected(value) {
      value && this.deleteValue(value);
    },
    setValue(value) {
      const index = this.value.findIndex(
        option => option.id === value.id && option.name === value.name
      );
      if (this.useRadio) {
        this.$emit('input', [value]);
      } else {
        if (index === -1 && this.value.length) {
          this.$emit('input', [...this.value, value]);
        } else if (!this.value.length) {
          this.$emit('input', [value]);
        }
      }
    },
    deleteValue(value) {
      const changedArray = this.value.filter(
        option => option.id !== value.id && option.name !== value.name
      );
      this.$emit('input', changedArray);
    },
    recursiveFilter(option, regex) {
      return (
        regex.test(option[this.nameKey]) ||
        (option[this.childrenKey] &&
          option[this.childrenKey].length &&
          (option[this.childrenKey] = option[this.childrenKey].filter(o =>
            this.recursiveFilter(o, regex)
          )).length)
      );
    },
    findOptionName(value, options) {
      let result = '';

      options.forEach(option => {
        const name = this.findOptionName(value, option.children);
        if (
          option[this.idKey] === value[this.idKey] &&
          option[this.nameKey] === value[this.nameKey]
        ) {
          result += result ? '; ' + option[this.nameKey] : option[this.nameKey];
        }
        result += result && name ? '; ' + name : name;
      });

      return result;
    },
    closeOptions() {
      this.hasOptions = false;
      this.focused = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/style/utils';

.tree-selector {
  width: 100%;
  margin-bottom: 20px;
  user-select: none;
  @include uiFont;

  &__top {
    margin-bottom: 5px;
    width: fit-content;
  }

  &__label {
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.3px;
    color: #ababba;
    width: fit-content;
  }

  &__required {
    color: #eb5757;
  }

  &__input {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 13px 14px;
    background: #f7f7fb;
    border-radius: 4px;
    border: none;
    cursor: pointer;
  }

  &__wrapper {
    position: relative;
    width: 100%;
  }

  &__left {
    display: flex;
  }

  &__search-icon {
    margin-right: 10px;
    cursor: pointer;
  }

  &__dropdown-icon {
    cursor: pointer;
    transition: 0.3s;
    display: flex;
    justify-content: center;
    align-items: center;

    &_active {
      transform: rotate(180deg);
    }
  }

  &__title {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.3px;
    color: $ui-black;

    &--placeholder {
      color: #76767b;
    }

    &--search {
      @include uiFont;
      border: none;
      background-color: transparent;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: -0.3px;
    }
  }

  &__options {
    position: absolute;
    width: 100%;
    padding: 16px;
    top: calc(100% + 4px);
    left: 0;
    background: #ffffff;
    box-shadow: 0 4px 10px rgba(43, 45, 52, 0.12);
    border-radius: 4px;
    z-index: 1;
    max-height: 250px;
    overflow-y: auto;
  }

  &__count {
    width: 100%;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: -0.3px;
    color: #76767b;
    padding-bottom: 12px;
    border-bottom: 1px solid #dfe0eb;
    margin-bottom: 16px;
  }
}
</style>
